/**
 * Dev-only persona override read from URL query params and persisted to
 * sessionStorage for the life of the browser tab. Mirrors
 * `devHelperRoleOverride.ts` so design passes can preview any persona
 * without going through onboarding.
 *
 * Supported params (all optional, all dev-only):
 *   ?dev_gender=AFAB|AMAB
 *   ?dev_primaryIntent=preserve|grow|donate|learn
 *   ?dev_treatmentGoal=egg-freezing|embryo-freezing|sperm-freezing|
 *                      egg-preserve|embryo-preserve|sperm-preserve|
 *                      pregnancy|egg-donation|sperm-donation|surrogacy|
 *                      adoption|exploring
 *
 * Example:
 *   localhost:8086/home?dev_gender=AFAB&dev_primaryIntent=preserve&dev_treatmentGoal=egg-freezing
 *
 * Captured at module load. Overrides are read by `useUserPersona` and
 * applied on top of (or instead of) the real patient record.
 */
import type {
  PersonaGender,
  PersonaPrimaryIntent,
} from "./useUserPersona";

type DevPersonaOverride = {
  gender: PersonaGender | null;
  primaryIntent: PersonaPrimaryIntent | null;
  treatmentGoal: string | null;
};

const STORAGE_KEY = "__moon_dev_persona";

const VALID_GENDERS: PersonaGender[] = ["AFAB", "AMAB", "unknown"];
const VALID_INTENTS: PersonaPrimaryIntent[] = [
  "preserve",
  "grow",
  "donate",
  "learn",
  "unknown",
];
const VALID_GOALS = [
  "egg-freezing",
  "embryo-freezing",
  "sperm-freezing",
  "egg-preserve",
  "embryo-preserve",
  "sperm-preserve",
  "pregnancy",
  "egg-donation",
  "sperm-donation",
  "surrogacy",
  "adoption",
  "exploring",
];

function asGender(v: string | null): PersonaGender | null {
  return v !== null && (VALID_GENDERS as string[]).includes(v)
    ? (v as PersonaGender)
    : null;
}

function asIntent(v: string | null): PersonaPrimaryIntent | null {
  return v !== null && (VALID_INTENTS as string[]).includes(v)
    ? (v as PersonaPrimaryIntent)
    : null;
}

function asGoal(v: string | null): string | null {
  return v !== null && VALID_GOALS.includes(v) ? v : null;
}

export const DEV_PERSONA_OVERRIDE: DevPersonaOverride = (() => {
  const empty: DevPersonaOverride = {
    gender: null,
    primaryIntent: null,
    treatmentGoal: null,
  };
  if (!__DEV__ || typeof window === "undefined") return empty;

  const params = new URLSearchParams(window.location.search);
  const fromUrl: DevPersonaOverride = {
    gender: asGender(params.get("dev_gender")),
    primaryIntent: asIntent(params.get("dev_primaryIntent")),
    treatmentGoal: asGoal(params.get("dev_treatmentGoal")),
  };

  const hasAnyFromUrl =
    fromUrl.gender || fromUrl.primaryIntent || fromUrl.treatmentGoal;

  if (hasAnyFromUrl) {
    try {
      window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    } catch {
      /* sessionStorage may be disabled — fall through */
    }
    return fromUrl;
  }

  try {
    const stored = window.sessionStorage?.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<DevPersonaOverride>;
      return {
        gender: asGender(parsed.gender ?? null),
        primaryIntent: asIntent(parsed.primaryIntent ?? null),
        treatmentGoal: asGoal(parsed.treatmentGoal ?? null),
      };
    }
  } catch {
    /* sessionStorage may be disabled or invalid JSON */
  }

  return empty;
})();
